import moment from "moment";

// serializer
import CentralLabOrderListSerializer from "issara-sdk/types/CentralLabOrderListSerializer_apps_LAB";
import CentralLabTestSerializer from "issara-sdk/types/CentralLabTestSerializer_apps_LAB";
import DoctorFeeOrderListPreviewSerializer from "issara-sdk/types/DoctorFeeOrderListPreviewSerializer_apps_DFC";
import DrugSerializer from "issara-sdk/types/DrugSerializer_apps_TPD";
import DoctorOrderListSerializer from "issara-sdk/types/DoctorOrderListSerializer_apps_DPO";
import EncounterPatientSerializer from "issara-sdk/types/EncounterPatientSerializer_core";
import MiscellaneousOrderSerializer from "issara-sdk/types/MiscellaneousOrderSerializer_core";

import formatComma from "react-lib/utils/formatComma";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

import {
  BTN_ACTS,
  ORDER_STATUS,
  type EncounterType,
  type LogKind,
  type OrderLog,
} from "../common/sequence/PatientOrdersV4";
import { ORDER_KIND_LABELS, orderKindOf, type OrderKind } from "../common/sequence/PatientListV4";
import type { ChipV4Tone } from "../common/ui";
import type {
  CatalogItem,
  DoseRange,
  DrugInteraction,
  DrugLabInteraction,
  GroupDuplicate,
  PackageEntitlement,
  SheetLine,
} from "../common/sequence/OrderSheetV4";

// ---------- หมวดคำสั่ง ----------

export type OrderCategory = OrderKind | "note";

export type SheetCategory = OrderCategory | "df" | "misc";

export const ORDER_DEF: Record<OrderCategory, [string, string[]]> = {
  lab: ["LAB", ["Request", "Collected", "Partial Report", "Approved"]],
  imaging: ["IMG", ["Requested", "In Progress", "Approved"]],
  treatment: ["PRO", ["Request (รอทำหัตถการ)", "Performed (ทำหัตถการแล้ว)"]],
  drug: ["DRG", ["Request", "Verify", "Check", "Deliver", "Received"]],
  supply: ["SUP", ["Ordered", "Deliver"]],
  note: ["NTE", ["Pending", "Complete"]],
};

export const SHEET_CATEGORIES: SheetCategory[] = [
  "lab",
  "imaging",
  "treatment",
  "drug",
  "supply",
  "note",
  "df",
  "misc",
];

export const CATEGORY_LABELS: Record<SheetCategory, string> = {
  ...ORDER_KIND_LABELS,
  note: "Note Order",
  df: "Doctor Fee",
  misc: "เบ็ดเตล็ด",
};

export const CATEGORY_TONES: Record<SheetCategory, ChipV4Tone> = {
  lab: "purple",
  imaging: "sky",
  treatment: "orange",
  drug: "teal",
  supply: "grey",
  note: "purple-soft",
  df: "ok",
  misc: "warn",
};

export const getCategory = (order: DoctorOrderListSerializer): OrderCategory | undefined => {
  const code: string = order.specific_type_code ?? "";

  if (code === "doctornoteorder") {
    return "note";
  }

  return orderKindOf(code);
};

// ---------- ใบคำสั่ง ----------

/** central-lab-test พร้อม lab_code / price ที่ SDK ไม่ได้ระบุ */
export type LabTest = CentralLabTestSerializer & {
  lab_code: string;
  price?: number | null;
};

export type SheetRow = {
  key: string;
  name: string;
  outLab?: boolean;
  summary?: string;
  amount: number;
  draftIndex?: number;
  draft?: SheetLine;
  missing?: string[];
  /** ข้อความสิทธิ์แพ็กเกจที่ใช้กับรายการนี้ */
  pkgText?: string;
  /** แพ้ยา: สิ่งที่แพ้ และเหตุผลที่แพทย์สั่งต่อ */
  adr?: { adversary: string; reason: string };
  /** เตือนขนาดยาเกินต่อวัน */
  doseWarning?: string;
};

/** รายการจากผลค้นหา / รายการโปรด */
export type PickItem = {
  key: string;
  category: SheetCategory;
  name: string;
  price: number | null;
  lab?: LabTest;
  item?: CatalogItem;
  /** รายละเอียดที่กรอกไว้ตอนกดดาวจากการ์ด */
  fields?: Record<string, string>;
  summary?: string;
};

/** ช่องค้นหารายการ: แสดงผลไม่เกิน / พิมพ์อย่างน้อย */
export const SEARCH_MAX_RESULTS = 6;

export const SEARCH_MIN_QUERY = 3;

export const IMAGING_SIDES = ["-", "ซ้าย", "ขวา", "สองข้าง"];

export const PREGNANCY = ["ไม่ตั้งครรภ์", "อาจตั้งครรภ์", "ตั้งครรภ์"];

export const DRUG_PRIORITIES: [string, string][] = [
  ["ROUTINE", "Routine"],
  ["STAT", "STAT"],
];

export type DrugOrderType = "HOME_OPD" | "ONE_DOSE" | "STAT";

export const drugTypeOf = (fields: Record<string, string>): DrugOrderType => {
  if (fields.priority === "STAT") {
    return "STAT";
  }

  return fields.home === "Y" ? "HOME_OPD" : "ONE_DOSE";
};

/** PRN หรือความถี่ที่ไม่มีครั้งต่อวัน (ตามแพทย์สั่ง): ไม่มีจำนวนวัน กรอก QTY เอง */
export const isAsNeeded = (fields: Record<string, string>, frequencies: Map<string, number>) =>
  !!fields.prn?.trim() || (!!fields.frequency && !frequencies.get(fields.frequency));

/** จำนวนจ่าย: พิมพ์เอง หรือ dose × ครั้งต่อวัน × จำนวนวัน ปัดขึ้นเป็นหน่วยขาย */
export const drugQuantity = (line: SheetLine, frequencies: Map<string, number>) => {
  const { fields } = line;

  if (Number(fields.qty) > 0) {
    return Number(fields.qty);
  }

  if (fields.prn?.trim()) {
    return 0;
  }

  const detail: Record<string, any> = line.raw.detail ?? {};
  const isHome = fields.home === "Y";
  const unit = Number(fields.unit);
  const perDay = isHome ? (frequencies.get(fields.frequency) ?? 0) : 1;
  const days = isHome ? Number(fields.duration) : Number(fields.duration) || 1;
  const stockSize =
    unit === detail.stock_unit ? 1 : unit === detail.base_unit ? Number(detail.stock_size) : 0;

  if (!stockSize) {
    return 0;
  }

  return Math.ceil((Number(fields.dose) * perDay * days) / stockSize) || 0;
};

/** ข้อความเตือนเมื่อ dose × ครั้งต่อวัน เกินค่าสูงสุดต่อวัน */
export const withDoseWarning = (line: SheetLine, frequencies: Map<string, number>): SheetLine => {
  const range: DoseRange | undefined = line.raw.doseRange;
  const perDay = Number(line.fields.dose) * (frequencies.get(line.fields.frequency) ?? 0);
  const isOver = !!range?.maxPerDay && range.unit === line.fields.unit && perDay > range.maxPerDay;
  const doseWarning = isOver
    ? `ขนาดยาเกินค่าสูงสุดต่อวัน (≤ ${range.maxPerDay} ${range.unitName}/วัน · สั่ง ${perDay} ${range.unitName}/วัน) · ตรวจสอบก่อนยืนยัน`
    : "";

  return line.raw.doseWarning === doseWarning || (!line.raw.doseWarning && !doseWarning)
    ? line
    : { ...line, raw: { ...line.raw, doseWarning } };
};

/** duplicated_detail (html) → ชื่อกลุ่ม และชื่อยาที่ซ้ำ */
export const parseGroupDuplicate = (html: string): GroupDuplicate | null => {
  const title = html
    .split(/<br\/?>/)[0]
    .replace(/<[^>]+>/g, "")
    .trim();
  const group = /^(.*?)(?: \([^)]*\))?$/.exec(title)?.[1];
  const names = Array.from(html.matchAll(/\d+\.<\/b> <b>\[[^\]]*\] (.*?)<\/b>/g), (match) =>
    shortName(match[1]),
  );

  return group && names.length > 0 ? { group, names: [...new Set(names)] } : null;
};

export const shortName = (name: string) =>
  name
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const parseImagingNote = (note?: string) => {
  const fields: Record<string, string> = {};
  const views: string[] = [];

  for (const part of (note ?? "").split(" · ").filter(Boolean)) {
    if (part.startsWith("Indication: ")) {
      fields.ind = part.slice("Indication: ".length);
    } else if (IMAGING_SIDES.includes(part)) {
      fields.side = part;
    } else if (PREGNANCY.includes(part)) {
      fields.preg = part;
    } else {
      views.push(part);
    }
  }

  return { ...fields, view: views.join(" · ") };
};

export const formatImagingNote = (fields: Record<string, string>) =>
  [
    fields.side && fields.side !== "-" ? fields.side : "",
    fields.view,
    fields.preg,
    fields.ind ? `Indication: ${fields.ind}` : "",
  ]
    .filter(Boolean)
    .join(" · ")
    .slice(0, 255);

/** จับคู่รายการกับสิทธิ์แพ็กเกจตามลำดับ ใช้ได้เมื่อคงเหลือพอกับจำนวน */
export const allocatePackages = (
  lines: { key: string; product?: number | null; quantity: number }[],
  packages: PackageEntitlement[],
) => {
  const left = packages.map((item) => item.left);
  const used = new Map<string, PackageEntitlement & { before: number }>();

  for (const line of lines) {
    const index = packages.findIndex(
      (item, i) => item.product === line.product && left[i] >= line.quantity,
    );

    if (index >= 0) {
      used.set(line.key, { ...packages[index], before: left[index] });
      left[index] -= line.quantity;
    }
  }

  return used;
};

export const supplyTypeOf = (line: SheetLine) =>
  line.fields.home === "Y" ? "HOME_OPD" : line.fields.use;

/** Regimen ตามช่วงวัย: 1 เด็ก (อายุต่ำกว่า 15 ปี), 2 ผู้ใหญ่ ถ้ายาไม่มีสูตรของช่วงวัยนั้นใช้ตัวแรก */
export const pickRegimen = (items: Record<string, any>[]) => {
  const age = Number(items[0]?.patient_age_year) < 15 ? 1 : 2;
  const regimen = items.find((item) => item.age === age) ?? items[0];
  const weight =
    Number(regimen?.encounter_weight) || (regimen?.calculation_factor_name === "NO_FACTOR" ? 1 : 0);

  return regimen && weight ? { regimen, weight } : null;
};

export const isCapsuleDrug = (detail?: Partial<DrugSerializer>) =>
  /แคปซูล|capsule/i.test(`${detail?.dosage_form_name ?? ""} ${detail?.stock_unit_name ?? ""}`);

/** รายการในใบคำสั่งที่ยังไม่ยืนยัน */
export const isPendingLine = (line: { draft?: unknown; draftIndex?: number }) =>
  !!line.draft || line.draftIndex !== undefined;

/** กลุ่มรายการในใบคำสั่ง พร้อมจำนวน In / Out Lab และยอดรวม */
export const sheetGroupOf = <C extends string, L extends { outLab?: boolean; amount: number }>(
  category: C,
  lines: L[],
) => {
  const outLab = lines.filter((line) => line.outLab).length;
  const inLab = category === "lab" ? lines.length - outLab : 0;

  return {
    category,
    lines,
    labText: [outLab ? `Out Lab ${outLab}` : "", inLab ? `In Lab ${inLab}` : ""]
      .filter(Boolean)
      .join(" · "),
    subtotal: lines.reduce((sum, line) => sum + line.amount, 0),
  };
};

export const pickOfLine = (line: SheetRow, test?: LabTest): PickItem | undefined => {
  if (test) {
    return {
      key: `lab_${test.lab_code}`,
      category: "lab",
      name: test.name,
      price: test.price ?? null,
      lab: test,
      summary: line.summary,
    };
  }

  const draft = line.draft;

  if (!draft) {
    return undefined;
  }

  return {
    key: draft.itemKey,
    category: draft.kind,
    name: draft.name,
    price: draft.masterPrice,
    item: {
      key: draft.itemKey,
      kind: draft.kind,
      id: draft.product,
      name: draft.name,
      price: draft.masterPrice,
      raw: draft.raw,
    },
    fields: draft.fields,
    summary: line.summary,
  };
};

/** รายการที่ควรตรวจทานก่อนส่ง: ยาที่แพ้แต่ยืนยันสั่ง ขนาดยาเกิน ยาตีกัน และยาซ้ำกลุ่ม */
export const getReviewItems = (lines: SheetLine[]) => {
  const items: { icon: string; text: string }[] = [];
  const groups = new Map<string, Set<string>>();
  const interactionIds = new Set<number>();

  for (const line of lines) {
    if (line.raw?.adr && line.fields.adrNote) {
      items.push({
        icon: "🔴",
        text: `แพ้ยา: ${line.name} · แพทย์ยืนยันสั่งต่อ (${line.fields.adrNote})`,
      });
    }

    const interaction: { drugs: DrugInteraction[]; labs: DrugLabInteraction[] } | undefined =
      line.raw?.interaction;

    for (const group of interaction?.drugs ?? []) {
      if (interactionIds.has(group.id)) {
        continue;
      }

      interactionIds.add(group.id);
      items.push({
        icon: "🟠",
        text: `ยาตีกัน: ${group.names.join(" + ")} · ${SEVERITY_TEXT[group.severity ?? 5]}${line.fields.interactionNote ? ` (${line.fields.interactionNote})` : ""}`,
      });
    }

    for (const lab of interaction?.labs ?? []) {
      items.push({
        icon: "🟠",
        text: `ยากับผลแลป: ${lab.name}${lab.summary ? ` · ${lab.summary}` : ""}`,
      });
    }

    if (line.raw?.doseWarning) {
      items.push({ icon: "🟡", text: `${line.name} · ${line.raw.doseWarning}` });
    }

    for (const duplicate of (line.raw?.groupDuplicates ?? []) as GroupDuplicate[]) {
      const names = groups.get(duplicate.group) ?? new Set<string>();

      for (const name of [shortName(line.name), ...duplicate.names]) {
        names.add(name);
      }

      groups.set(duplicate.group, names);
    }
  }

  for (const [group, names] of groups) {
    items.push({ icon: "🟡", text: `ซ้ำกลุ่มยา ${group}: ${[...names].join(" + ")}` });
  }

  return items;
};

export const formatLabSummary = (item: Record<string, any>, test?: LabTest) => {
  const tat = formatTat(test);

  return [
    URGENCY_LABELS[item.urgency] ?? item.urgency,
    test?.lab_division_name ? `ส่งตรวจ ${test.lab_division_name}` : "",
    tat ? `TAT ~${tat}` : "",
    item.note,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const formatPackage = (
  use: (PackageEntitlement & { before: number }) | undefined,
  price: number,
) =>
  use
    ? `อยู่ในแพ็กเกจ ${use.packageName} · คงเหลือ ${use.before} · ราคาปกติ ${formatComma(price, "en-US", false)} ฿`
    : undefined;

export const formatPrice = (value: number | string | null) =>
  value == null ? "– ฿" : `${formatComma(value, "en-US", false)} ฿`;

/** ราคาในผลค้นหา (เบ็ดเตล็ดที่ไม่มีราคา Master = ระบุราคาเอง) */
export const formatPickPrice = (pick: Pick<PickItem, "category" | "price">) =>
  Number(pick.price) || pick.category !== "misc" ? formatPrice(pick.price) : "ระบุราคาเอง";

/** ส่งตรวจ Inlab / Outlab: ปลายทางที่แลปนั้นทำไม่ได้กดไม่ได้ */
export const labDestinationOptions = (labTypeLabel?: string) => {
  const isOut = labTypeLabel === "Out lab";

  return [
    { value: "IN", text: isOut ? "Inlab (ไม่มี)" : "Inlab", disabled: isOut },
    { value: "OUT", text: isOut ? "Outlab" : "Outlab (ไม่มี)", disabled: !isOut },
  ];
};

/** ระยะเวลารอผลแลปจาก Master เช่น "2 ชม." "1 ชม. 30 นาที" */
export const formatTat = (test?: LabTest) => {
  const hours = test?.turn_around_time_hr ?? 0;
  const minutes = test?.turn_around_time_min ?? 0;

  return [hours ? `${hours} ชม.` : "", minutes ? `${minutes} นาที` : ""].filter(Boolean).join(" ");
};

// ---------- คำสั่งที่ยืนยันแล้ว ----------

/** แถวในตารางคำสั่งที่ยืนยันแล้ว */
export type DoneRow = {
  key: string;
  category: SheetCategory;
  status: string;
  items: string[];
  isCancelled: boolean;
  isEditing: boolean;
  isLocked: boolean;
  canEdit: boolean;
  canCancel: boolean;
  /** order_time: เวลาสร้าง ยังไม่อยู่ใน type ของ SDK */
  order?: DoctorOrderListSerializer & { order_time?: string };
  queueOrder?: NonNullable<EncounterType["doctor_orders"]>[number];
  labOrder?: CentralLabOrderListSerializer;
  df?: DoctorFeeOrderListPreviewSerializer;
  misc?: MiscellaneousOrderSerializer;
};

export const cancelKeyOf = (row: DoneRow) => {
  if (row.df) {
    return `${BTN_ACTS.CANCEL_FEE}_df_${row.df.id}`;
  }

  return row.misc
    ? `${BTN_ACTS.CANCEL_FEE}_misc_${row.misc.id}`
    : `${BTN_ACTS.CANCEL}_${row.order?.id}`;
};

/** รหัส Order ที่แสดง เช่น LAB-123 DF-4 MISC-5 */
export const codeOf = (row: DoneRow) => {
  if (row.df) {
    return `DF-${row.df.id}`;
  }

  return row.misc
    ? `MISC-${row.misc.id}`
    : `${ORDER_DEF[row.category as OrderCategory][0]}-${row.order?.id}`;
};

export const createdTimeOf = (row: DoneRow) => {
  const created = row.df?.created_at ?? row.misc?.created;

  return created
    ? formatISOtoBEDateTime(created).timeShort
    : formatTime(row.order?.order_time ?? row.queueOrder?.order_time ?? row.labOrder?.order_time);
};

export const priceOf = (row: DoneRow): number | string | undefined =>
  row.df?.price_total ?? row.misc?.price_total ?? row.order?.price?.price_total;

// ---------- สถานะ / คิว ----------

export type SpecificStatus = {
  name?: string;
  items: { status?: string; is_authorized?: boolean }[];
};

type OrderSummaryCache = NonNullable<
  EncounterPatientSerializer["doctor_orders"]
>[number]["order_summary_detail_cache"];

export const URGENCY_LABELS: Record<string, string> = {
  ROUTINE: "Routine",
  STAT: "STAT",
};

export const SEVERITY_TEXT: Record<number, string> = {
  1: "Minor",
  2: "Moderate",
  3: "Major",
  4: "Contraindication",
  5: "ไม่ระบุ",
};

/** order_summary_detail_cache ของแถวคิวมาเป็นข้อความ JSON */
export const parseStatus = (cache?: OrderSummaryCache | string): SpecificStatus => {
  try {
    const data: OrderSummaryCache = typeof cache === "string" ? JSON.parse(cache) : cache;

    return {
      name: data?.specific_status?.name ?? undefined,
      items: Object.values(data?.specific_items_status ?? {}),
    };
  } catch {
    return { items: [] };
  }
};

/** ขั้นของ Order ID จากสถานะของคำสั่งแต่ละประเภท */
export const getStep = (
  category: OrderCategory,
  order: DoctorOrderListSerializer,
  status: SpecificStatus,
): number => {
  const name = status.name ?? "";

  if (category === "lab") {
    const reported = status.items.filter((item) => item.status === "REPORTED");

    if (reported.length > 0 && reported.length === status.items.length) {
      return status.items.every((item) => item.is_authorized) ? 3 : 2;
    }

    if (reported.length > 0) {
      return 2;
    }

    return status.items.some((item) => item.status !== "ORDERED") ? 1 : 0;
  }

  if (category === "imaging") {
    return { IN_PROGRESS: 1, REPORTED: 2 }[name] ?? 0;
  }

  if (category === "treatment") {
    return name === "PERFORMED" ? 1 : 0;
  }

  if (category === "drug") {
    return (
      { VERIFIED: 1, PRINTED: 2, CHECKED: 2, DELIVERED: 3, TRANSPORTED: 3, RECEIVED: 4 }[name] ?? 0
    );
  }

  if (category === "supply") {
    return ["DELIVERED", "RECEIVED"].includes(name) ? 1 : 0;
  }

  return order.order_status === ORDER_STATUS.PERFORMED ? 1 : 0;
};

/** order_time ของแถวคิวเป็น ISO แสดงเวลา ของรายการแลป "DD/MM/YYYY [HH:mm]" แสดงตามที่มา */
export const formatTime = (value?: string) =>
  moment(value, moment.ISO_8601, true).isValid()
    ? formatISOtoBEDateTime(value).timeShort
    : (value ?? "");

// ---------- Log ----------

/** สถานะของ Order หลัง action ใน Log แต่ละประเภท */
const LOG_STATUS: Record<LogKind, Record<string, string>> = {
  drug: {
    REQUEST: "Request",
    UNVERIFY: "Request",
    VERIFY: "Verify",
    UNCHECK: "Verify",
    CHECK: "Check",
    UNDELIVER: "Check",
    DELIVER: "Deliver",
    RECEIVE: "Received",
    CANCEL: "Cancelled",
  },
  supply: {
    REQUEST: "Ordered",
    UNDELIVER: "Ordered",
    DELIVER: "Deliver",
    CANCEL: "Cancelled",
  },
  imaging: {
    ORDER: "Requested",
    UNREGISTER: "Requested",
    UNEXECUTE: "Requested",
    REGISTER: "In Progress",
    EXECUTE: "In Progress",
    REPORT: "Approved",
    FINALIZE: "Approved",
    APPROVE: "Approved",
    CANCEL: "Cancelled",
    RIS_CANCEL: "Cancelled",
  },
};

const LOG_CREATE = ["REQUEST", "ORDER"];

const LOG_EDIT = ["EDIT", "EDIT_QUANTITY", "RIS_EDIT"];

export const isLogKind = (category: string): category is LogKind => category in LOG_STATUS;

/** แถว Log: สร้าง / แก้ไข / เปลี่ยนสถานะ / ยกเลิก ใหม่สุดอยู่บน */
export const getLogRows = (kind: LogKind, items: OrderLog["items"]) => {
  const rows: (OrderLog["items"][number] & { status: string; text: string })[] = [];
  let status = "";

  for (const item of items) {
    const action = ("action_name" in item && item.action_name) || String(item.action);
    const nextStatus = LOG_STATUS[kind][action];

    if (nextStatus) {
      status = nextStatus;
    }

    let text = "";

    if (LOG_CREATE.includes(action)) {
      text = "สร้าง Order";
    } else if (LOG_EDIT.includes(action)) {
      text = "แก้ไข Order";
    } else if (nextStatus === "Cancelled") {
      text = item.note ? `ยกเลิกโดยแพทย์ (${item.note})` : "ยกเลิกโดยแพทย์";
    } else if (nextStatus) {
      text = `เปลี่ยนสถานะเป็น ${nextStatus}`;
    }

    if (text) {
      rows.push({ ...item, status, text });
    }
  }

  return rows.reverse();
};
